import React from 'react';

/**
 * Filter controls for the Trade Tracker table.
 * Provides filters for search, action, call options, dividend reinvestments,
 * source, brokerage, and symbol, along with a Clear button.
 */
export default function TradeTrackerFilters({
  filters = {},
  onFilterChange,
  setFilters,
  uniqueBrokerages = [],
  uniqueSymbols = [],
  onClear,
}) {
  const handleChange = (name, value) => {
    if (onFilterChange) {
      onFilterChange(name, value);
    } else if (setFilters) {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="p-3 border-bottom bg-light">
      <div className="row g-2 align-items-end">
        <div className="col-md-3">
          <label htmlFor="filter-search" className="form-label small mb-1">Search</label>
          <input
            id="filter-search"
            type="text"
            className="form-control form-control-sm"
            value={filters.search || ''}
            onChange={(e) => handleChange('search', e.target.value)}
            placeholder="Symbol, account, broker..."
          />
        </div>
        <div className="col-md-2">
          <label htmlFor="filter-action" className="form-label small mb-1">Action</label>
          <select
            id="filter-action"
            aria-label="Action"
            className="form-select form-select-sm"
            value={filters.action || 'all'}
            onChange={(e) => handleChange('action', e.target.value)}
          >
            <option value="all">All</option>
            <option value="BUY">Buy</option>
            <option value="SELL">Sell</option>
            <option value="DIVIDEND_REINVESTMENT">Dividend Reinvestment</option>
          </select>
        </div>
        <div className="col-md-2">
          <label htmlFor="filter-covered-calls" className="form-label small mb-1">Call Options</label>
          <select
            id="filter-covered-calls"
            aria-label="Call Options"
            className="form-select form-select-sm"
            value={filters.coveredCalls || 'all'}
            onChange={(e) => handleChange('coveredCalls', e.target.value)}
          >
            <option value="all">All</option>
            <option value="exclude">Exclude</option>
            <option value="only">Only</option>
          </select>
        </div>
        <div className="col-md-2">
          <label htmlFor="filter-dividend-reinvestments" className="form-label small mb-1">Dividend Reinvestments</label>
          <select
            id="filter-dividend-reinvestments"
            aria-label="Dividend Reinvestments"
            className="form-select form-select-sm"
            value={filters.dividendReinvestments || 'all'}
            onChange={(e) => handleChange('dividendReinvestments', e.target.value)}
          >
            <option value="all">All</option>
            <option value="exclude">Exclude</option>
            <option value="only">Only</option>
          </select>
        </div>
        <div className="col-md-3">
          <label htmlFor="filter-source" className="form-label small mb-1">Source</label>
          <select
            id="filter-source"
            aria-label="Source"
            className="form-select form-select-sm"
            value={filters.source || 'all'}
            onChange={(e) => handleChange('source', e.target.value)}
          >
            <option value="all">All</option>
            <option value="manual">Manual</option>
            <option value="auto">Auto</option>
            <option value="hybrid">Hybrid</option>
            <option value="all:date-only">Date Only (All Sources)</option>
            <option value="all:known-time">No Date Only (All Sources)</option>
            <option value="manual:date-only">Manual + Date Only</option>
            <option value="manual:known-time">Manual (No Date Only)</option>
            <option value="auto:date-only">Auto + Date Only</option>
            <option value="auto:known-time">Auto (No Date Only)</option>
            <option value="hybrid:date-only">Hybrid + Date Only</option>
            <option value="hybrid:known-time">Hybrid (No Date Only)</option>
          </select>
        </div>
        <div className="col-md-5">
          <label htmlFor="filter-brokerage" className="form-label small mb-1">Brokerage</label>
          <select
            id="filter-brokerage"
            aria-label="Brokerage"
            className="form-select form-select-sm"
            value={filters.brokerage || 'all'}
            onChange={(e) => handleChange('brokerage', e.target.value)}
          >
            <option value="all">All</option>
            {uniqueBrokerages.map((brokerage) => (
              <option key={brokerage} value={brokerage}>{brokerage}</option>
            ))}
          </select>
        </div>
        <div className="col-md-5">
          <label htmlFor="filter-symbol" className="form-label small mb-1">Symbol</label>
          <select
            id="filter-symbol"
            aria-label="Symbol"
            className="form-select form-select-sm"
            value={filters.symbol || 'all'}
            onChange={(e) => handleChange('symbol', e.target.value)}
          >
            <option value="all">All</option>
            {uniqueSymbols.map((symbol) => (
              <option key={symbol} value={symbol}>{symbol}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <button
            type="button"
            className="btn btn-outline-secondary form-control form-control-sm"
            onClick={onClear}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}
